/**
 * Utility functions for the UI components
 */

/**
 * Combines class names, filtering out falsy values
 * A lightweight alternative to clsx/classnames
 */
export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

/**
 * Model IDs that do NOT support custom temperature / max_tokens (reasoning/restricted models).
 * Mirrors backend requires_restricted_params: o-series, gpt-chat-latest, and the GPT-5
 * / GPT-6 families (gpt-5 / gpt-5.1 / gpt-5.5 / mini; gpt-6-luna / gpt-6-*).
 * gpt-*-chat-* variants still accept temperature.
 * Used to hide temperature in Model Chat Settings and eval/prompt-optimization dialogs.
 * Scan every path/query token so APIM URLs ending in /chat/completions still match.
 */
const JUDGE_RESTRICTED_EXACT = new Set(['o1', 'o3', 'o4', 'gpt-5-mini', 'gpt-5.5-mini', 'gpt-chat-latest'])
const JUDGE_RESTRICTED_PREFIXES = ['o1-', 'o3-', 'o4-', 'gpt-5-mini', 'gpt-5.5-mini']

function tokenIsGpt5FamilyRestricted(name: string): boolean {
  if (name.includes('gpt-5-chat') || name.includes('gpt5-chat')) return false
  if (name.includes('gpt-5')) return true
  return name.includes('gpt5.') || name.includes('gpt5-') || name === 'gpt5' || name.endsWith('-gpt5')
}

function tokenIsGpt6FamilyRestricted(name: string): boolean {
  if (name.includes('gpt-6-chat') || name.includes('gpt6-chat')) return false
  if (name.includes('gpt-6')) return true
  return name.includes('gpt6.') || name.includes('gpt6-') || name === 'gpt6' || name.endsWith('-gpt6')
}

function tokenIsJudgeRestricted(name: string): boolean {
  if (name.includes('gpt-5-chat') || name.includes('gpt5-chat')) return false
  if (name.includes('gpt-6-chat') || name.includes('gpt6-chat')) return false
  if (name.includes('gpt-chat-latest')) return true
  if (tokenIsGpt5FamilyRestricted(name)) return true
  if (tokenIsGpt6FamilyRestricted(name)) return true
  return (
    JUDGE_RESTRICTED_EXACT.has(name) ||
    JUDGE_RESTRICTED_PREFIXES.some((p) => name.startsWith(p))
  )
}

/**
 * Returns true if the given model_id does not accept custom temperature (and max_tokens).
 * Use this to hide or disable those fields when the selected model is restricted.
 */
export function isJudgeModelRestricted(modelId: string | null | undefined): boolean {
  if (!modelId || typeof modelId !== 'string') return false
  const tokens = modelId
    .toLowerCase()
    .trim()
    .split(/[/\\?&#=\s]+/)
    .map((t) => t.replace(/^[:.]+|[.:]+$/g, ''))
    .filter(Boolean)
  return tokens.some(tokenIsJudgeRestricted)
}

/**
 * Deduplicates strings that consist of the exact same substring repeated multiple times
 * with no delimiters (e.g. "Azure OpenAI GPT-4.1 MiniAzure OpenAI GPT-4.1 Mini")
 * or comma-separated duplicates.
 * Also handles if the backend incorrectly returned an array.
 * 
 * @param input The potentially repeating string or array from backend
 * @returns The shortest repeating sequence, or the original string if no repeating pattern exists
 */
export function deduplicateModelString(input: string | string[] | undefined | null): string {
  if (!input) return ''
  const str = Array.isArray(input) ? input[0] : String(input)
  const trimmed = str.trim()
  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map((s) => s.trim()).filter(Boolean)
    if (parts.length > 0 && parts.every((p) => p === parts[0])) return parts[0]
  }
  const match = trimmed.match(/^(.+?)\1+$/)
  return match ? match[1].trim() : str
}
